import { IAdFoxBannerOnErrorError, IAdFoxBannerOnLoadData } from '@cian/adfox-component';
import { UnknownError } from '@cian/peperrors/shared';
import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { triggerAdsEvent } from '../../../../common/actions/adsEvents';
import { useApplicationContext } from '../../../../common/containers/ApplicationContext';
import { TThunkDispatch } from '../../../../common/types/redux';
import { EListingBannerType } from '../../../constants/banner';
import { usePageType } from '../usePageType';

enum EViewState {
  Hidden,
  Viewed,
  PostViewSent,
}

interface IParams {
  bannerType?: EListingBannerType | 'tgb_slider';
  position?: number;
}

export function useListingBanner({ bannerType, position }: IParams) {
  const pageType = usePageType();
  const { telemetry, logger } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const viewState = React.useRef<EViewState>(EViewState.Hidden);
  const deviceType = useDeviceType();

  const [bannerData, setBannerData] = React.useState<IAdFoxBannerOnLoadData | null>(null);
  const [isHidden, setIsHidden] = React.useState(false);

  const isMap = pageType === 'resultsForMap';

  const onClick = React.useCallback(() => {
    dispatch(
      triggerAdsEvent(false, deviceType, bannerData, {
        bannerType,
        position,
        isMap,
      }),
    );
  }, [bannerData, dispatch, position, bannerType, isMap]);

  const onLoad = React.useCallback(
    (data: IAdFoxBannerOnLoadData) => {
      setBannerData(data);

      telemetry.increment('serp.adfox.loaded');

      if (viewState.current === EViewState.Viewed) {
        dispatch(
          triggerAdsEvent(true, deviceType, data, {
            bannerType,
            position,
            isMap,
          }),
        );

        viewState.current = EViewState.PostViewSent;
      }
    },
    [dispatch, telemetry, bannerType, position, isMap],
  );

  const onStub = React.useCallback(() => {
    setIsHidden(true);
  }, []);

  const onError = React.useCallback(
    (error: IAdFoxBannerOnErrorError) => {
      setIsHidden(true);

      logger.warning(
        new UnknownError({
          message: error.message || 'Неизвестная ошибка',
          domain: `AdfoxListingBannerComponent#${error.type}`,
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error as any,
      );
    },
    [logger],
  );

  const onView = React.useCallback(() => {
    telemetry.increment('serp.adfox.visible');

    if (bannerData?.bundleParams.bannerId) {
      dispatch(
        triggerAdsEvent(true, deviceType, bannerData, {
          bannerType,
          position,
          isMap,
        }),
      );

      viewState.current = EViewState.PostViewSent;
    } else {
      viewState.current = EViewState.Viewed;
    }
  }, [telemetry, bannerData, bannerType, position, isMap, dispatch]);

  return {
    onClick,
    isHidden,
    onLoad,
    onError,
    onView,
    onStub,
  };
}
